import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ChevronDown, FileText, Presentation, Download } from "lucide-react";
import { INVESTOR_DECK } from "@/lib/investorDeck";
import { NAV_LINKS } from "@/data/content";
import { useLead } from "@/context/LeadContext";
import Icon from "@/components/Icon";

export default function Navbar() {
  const { openLead } = useLead();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (l) => {
    setMenuOpen(false);
    if (l.type === "route") return navigate(l.target);
    if (l.type === "modal") return openLead("contact");
    if (location.pathname !== "/") navigate("/", { state: { scrollTo: l.target } });
    else {
      const el = document.getElementById(l.target);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleBrand = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname !== "/") navigate("/");
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header className={`navbar ${scrolled ? "navbar--scrolled" : ""}`} data-testid="navbar">
      <div className="navbar__inner">
        <a className="navbar__brand" href="/" onClick={handleBrand} data-testid="nav-logo">
          <img src="/assets/logo-ums-header.png" alt="Proactive UMS+" className="navbar__logo" />
        </a>

        <nav className="navbar__links">
          {NAV_LINKS.map((l) => (
            <button key={l.label} className="navbar__link" onClick={() => handleNav(l)} data-testid={`nav-link-${l.label.toLowerCase()}`}>
              {l.label}
            </button>
          ))}
        </nav>

        <div className="navbar__actions">
          <button className="navbar__search" aria-label="Search" data-testid="nav-search">
            <Icon name="Search" size={18} />
          </button>
          <div className="nav-deck-group">
            <Button asChild variant="ghost" className="nav-deck-link"><Link to="/investors" onClick={() => setMenuOpen(false)} aria-current={location.pathname === '/investors' ? 'page' : undefined} data-testid="nav-investor-deck">Investor Deck</Link></Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" className="nav-deck-toggle" aria-label="Investor deck downloads" data-testid="nav-investor-downloads"><ChevronDown size={14} /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="nav-deck-menu" data-testid="nav-investor-menu">
                <DropdownMenuItem asChild><Link to="/investors" data-testid="nav-deck-view"><Presentation size={16} /> View investor deck</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><a href={INVESTOR_DECK.pdf} target="_blank" rel="noopener noreferrer" data-testid="nav-deck-open-pdf"><FileText size={16} /> Open PDF</a></DropdownMenuItem>
                <DropdownMenuItem asChild><a href={INVESTOR_DECK.pdf} download={INVESTOR_DECK.pdfFilename} data-testid="nav-deck-download-pdf"><Download size={16} /> Download PDF</a></DropdownMenuItem>
                <DropdownMenuItem asChild><a href={INVESTOR_DECK.pptx} download={INVESTOR_DECK.pptxFilename} data-testid="nav-deck-download-pptx"><Download size={16} /> Download PowerPoint</a></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <button className="btn btn--blue navbar__cta" onClick={() => openLead("demo")} data-testid="nav-request-demo">
            Request a Demo <Icon name="ArrowRight" size={16} />
          </button>
          <button className="navbar__burger" aria-label="Menu" aria-expanded={menuOpen} aria-controls="mobile-navigation" onClick={() => setMenuOpen((v) => !v)} data-testid="nav-burger">
            <span /><span /><span />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="navbar__mobile" id="mobile-navigation" data-testid="nav-mobile">
          <Button asChild variant="ghost" className="nav-deck-mobile-link"><Link to="/investors" onClick={() => setMenuOpen(false)} aria-current={location.pathname === '/investors' ? 'page' : undefined} data-testid="nav-mobile-investor-deck"><Presentation size={18} /> Investor Deck</Link></Button>
          <div className="nav-deck-mobile-downloads">
            <Button asChild variant="outline"><a href={INVESTOR_DECK.pdf} download={INVESTOR_DECK.pdfFilename} data-testid="nav-mobile-download-pdf"><Download size={16} /> PDF</a></Button>
            <Button asChild variant="outline"><a href={INVESTOR_DECK.pptx} download={INVESTOR_DECK.pptxFilename} data-testid="nav-mobile-download-pptx"><Download size={16} /> PPTX</a></Button>
          </div>
          {NAV_LINKS.map((l) => (
            <button key={l.label} className="navbar__mobile-link" onClick={() => handleNav(l)} data-testid={`nav-mobile-${l.label.toLowerCase()}`}>
              {l.label}
            </button>
          ))}
          <button className="btn btn--blue" data-testid="nav-mobile-request-demo" onClick={() => { setMenuOpen(false); openLead("demo"); }}>
            Request a Demo <Icon name="ArrowRight" size={16} />
          </button>
        </div>
      )}
    </header>
  );
}
